import React, { useState } from 'react';
import { Upload, message, Icon, Button, Spin, Input } from 'antd';
import client from '../../../api/graphql/client';
import { UPLOAD_TIMESHEET_ATTACHMENT, DELETE_TIMESHEET_ATTACHMENT } from '../mutations';
import { showSuccess, showError } from '../../common/notifications';
import { GET_USER_TIMESHEET } from '../queries';
import { uploadFileToAzureBlob } from '../../common';
import moment from 'moment';
import AttachmentLink from './AttachmentLink';

const { TextArea } = Input;

const TimesheetAttachments = ({ timesheet, endDate, readonly }) => {
  const { timesheetAttachments,startDate } = timesheet;
  const [uploading, setUploading] = useState(false);
  const [description, setDescription] = useState(`${moment(startDate).format('MMMM Do')} client timesheet`);

  const beforeUpload = file => {
    const isLt20M = file.size / 1024 / 1024 < 20;
    if (!isLt20M) {
      message.error('Image must smaller than 20MB!');
    }
    return isLt20M;
  };
  const uploadButton = uploading ? (
    <Button>
      <Spin style={{ padding: '5px' }} />
      <span style={{ padding: '5px' }}>Uploading..</span>
    </Button>
  ) : (
    <Button>
      <Icon type="upload" /> Upload Attachments
    </Button>
  );
  const uploadHandler = async ({ file }) => {
    setUploading(true);
    const { name } = file;
    const fileName = `${Date.now()}_${name}`;
    const url = await uploadFileToAzureBlob({
      fileName,
      file,
      blobContainerName: 'uploads',
    });
    client
      .mutate({
        mutation: UPLOAD_TIMESHEET_ATTACHMENT,
        variables: {
          timeSheetId: timesheet._id,
          url: url,
          description: description,
        },
        refetchQueries: [
          {
            query: GET_USER_TIMESHEET,
            variables: { endDate },
          },
        ],
      })
      .then(() => {
        showSuccess({ message: 'Updated Successfully' });
        setUploading(false);
      })
      .catch(err => showError({ message: err.message }));
  };

  const deleteHandler = id => {
    client
      .mutate({
        mutation: DELETE_TIMESHEET_ATTACHMENT,
        variables: {
          timeSheetId: timesheet._id,
          id: id,
        },
        refetchQueries: [
          {
            query: GET_USER_TIMESHEET,
            variables: { endDate },
          },
        ],
      })
      .then(() => {
        showSuccess({ message: 'Deleted Successfully' });
        setUploading(false);
      })
      .catch(err => showError({ message: err.message }));
  };

  return (
    <div className="p-4">
      {!readonly && (
        <div className="flex flex-col gap-4">
                    <div style={{
            background: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '8px',
            padding: '16px',
          }}>
            <div style={{ 
              fontSize: '14px',
              color: '#262626',
              lineHeight: '1.8'
            }}>
              <div style={{ fontWeight: '500', marginBottom: '8px', color: '#1890ff' }}>
                <Icon type="info-circle" style={{ marginRight: '8px' }} />
                Instructions:
              </div>
              <div style={{ paddingLeft: '24px' }}>
                You may upload supplementary documents, if available (i.e client timesheets etc.)
              </div>
            </div>
          </div>
          <div style={{
            background: '#fafafa',
            border: '1px solid #d9d9d9',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '16px',
          }}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontSize: '14px',
                fontWeight: '500',
                color: '#262626'
              }}>
                Brief Description
              </label>
              <TextArea
                name="description"
                value={description}
                onChange={e => {
                  setDescription(e.target.value);
                }}
                placeholder="Enter description..."
                rows={3}
                style={{ 
                  width: '100%',
                  resize: 'vertical',
                  fontSize: '14px'
                }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Upload
                name="attachment"
                customRequest={value => uploadHandler(value)}
                beforeUpload={beforeUpload}
                multiple={false}
                showUploadList={false}
                disabled={uploading}
              >
                {uploadButton}
              </Upload>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {timesheetAttachments?.map(item => {
          return (
            <div 
              key={item._id}
              className="p-4"
              style={{
                border: '1px solid #e8e8e8',
                borderRadius: '8px',
                background: '#fff',
                transition: 'all 0.3s ease',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                e.currentTarget.style.borderColor = '#d9d9d9';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0, 0, 0, 0.05)';
                e.currentTarget.style.borderColor = '#e8e8e8';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Icon
                      type="paper-clip"
                      style={{ color: '#1890ff', fontSize: '16px', flexShrink: 0 }}
                    />
                    <AttachmentLink url={item.url} />
                  </div>
                  {item.description && (
                    <p style={{ 
                      margin: '4px 0 0 24px', 
                      fontSize: '13px', 
                      color: '#8c8c8c',
                      lineHeight: '1.5',
                    }}>
                      {item.description}
                    </p>
                  )}
                </div>
                {timesheet.status === 'Not Submitted' && !readonly && (
                  <Button
                    type="link"
                    size="small"
                    onClick={() => {
                      deleteHandler(item._id);
                    }}
                    style={{ padding: '4px', flexShrink: 0, color: '#ff4d4f', alignSelf: 'flex-start', marginTop: '-4px' }}
                  >
                    <Icon type="delete" style={{ color: '#ff4d4f' }} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {(!timesheetAttachments || timesheetAttachments.length === 0) && readonly && (
        <div style={{ 
          textAlign: 'center', 
          padding: '32px 20px',
          color: '#8c8c8c',
          fontSize: '14px',
          background: '#fafafa',
          borderRadius: '8px',
          border: '1px dashed #d9d9d9',
        }}>
          <Icon type="inbox" style={{ fontSize: '32px', marginBottom: '8px', color: '#bfbfbf' }} />
          <div>No Attachments</div>
        </div>
      )}
    </div>
  );
};

export default TimesheetAttachments;
